import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EditorData } from "../../../utils/editorUtils";
import { MapPanoRepository } from "../Repositories/MapPanoRepository";
import { MapPoiRepository } from "../Repositories/MapPoiRepository";
import { useRestAuth } from "../../../hooks/useRestAuth";

/**
 * Cache for map layer data indexed by layer ID
 */
interface LayerDataCache {
  [layerId: number]: EditorData<"map">;
}

/**
 * Context interface for managing map layer data caching and retrieval
 */
interface IMapDataContext {
  layerDataCache: LayerDataCache;
  getLayerData: (layerId: number, forceRefresh?: boolean) => Promise<EditorData<"map">>;
  updateLayerData: (layerId: number, newData: Partial<EditorData<"map">>) => void;
  clearCache: () => void;
  forceReload: () => void;
  updateUnusedItemsInCache: () => Promise<void>;
  markAllLayersStale: () => void;
  clearGlobalChangesFlag: () => void;
  hasGlobalChanges: boolean;
  cacheVersion: number;
}

const initialState: IMapDataContext = Object.freeze({
  layerDataCache: {},
  getLayerData: async () => ({} as EditorData<"map">),
  updateLayerData: () => {},
  clearCache: () => {},
  forceReload: () => {},
  updateUnusedItemsInCache: async () => {},
  markAllLayersStale: () => {},
  clearGlobalChangesFlag: () => {},
  hasGlobalChanges: false,
  cacheVersion: 0,
});

const MapDataContext = createContext<IMapDataContext>(initialState);

/**
 * Provider component for map data caching context
 */
export const MapDataProvider = ({ children }: PropsWithChildren<{}>) => {
  const [layerDataCache, setLayerDataCache] = useState<LayerDataCache>({});
  const [hasGlobalChanges, setHasGlobalChanges] = useState(false);
  const [cacheVersion, setCacheVersion] = useState(0);
  const restAuth = useRestAuth();

  // Use ref to avoid recreation of getLayerData callback when cache updates
  const layerDataCacheRef = useRef<LayerDataCache>({});
  
  // Keep ref in sync with state
  useEffect(() => {
    layerDataCacheRef.current = layerDataCache;
  }, [layerDataCache]);

  // Map data repositories
  const mapPanoRepository = useMemo(() => new MapPanoRepository(), []);
  const mapPoiRepository = useMemo(() => new MapPoiRepository(), []);

  /**
   * Retrieves layer data from cache or fetches from API
   * @param layerId - The ID of the layer to fetch
   * @param forceRefresh - Whether to bypass cache and fetch fresh data
   */
  const getLayerData = useCallback(
    async (layerId: number, forceRefresh = false): Promise<EditorData<"map">> => {
      const shouldRefresh = forceRefresh || hasGlobalChanges;
      
      // Return cached data if available and refresh not needed
      if (!shouldRefresh && layerDataCacheRef.current[layerId]) {
        return layerDataCacheRef.current[layerId];
      }

      // Fetch fresh data from API
      if (restAuth) {
        const [
          panoLinks,
          poiLinks,
          unlinkedPanoLinks,
          unlinkedPoiLinks,
          unusedPanoLinks,
          unusedPoiLinks
        ] = await Promise.all([
          mapPanoRepository.get(restAuth, layerId),
          mapPoiRepository.get(restAuth, layerId),
          mapPanoRepository.get_unlinked(restAuth, layerId),
          mapPoiRepository.get_unlinked(restAuth, layerId),
          mapPanoRepository.get_unused(restAuth),
          mapPoiRepository.get_unused(restAuth)
        ]);

        const layerData: EditorData<"map"> = {
          panorama_location: panoLinks,
          poi_location: poiLinks,
          unlinked_panorama_location: unlinkedPanoLinks,
          unlinked_poi_location: unlinkedPoiLinks,
          unused_panorama_location: unusedPanoLinks,
          unused_poi_location: unusedPoiLinks,
        };

        // Store in cache
        setLayerDataCache(prev => ({
          ...prev,
          [layerId]: layerData
        }));

        return layerData;
      }

      return {} as EditorData<"map">;
    },
    [hasGlobalChanges, restAuth, mapPanoRepository, mapPoiRepository]
  );

  /**
   * Updates cached data for a specific layer
   */
  const updateLayerData = useCallback(
    (layerId: number, newData: Partial<EditorData<"map">>) => {
      setLayerDataCache(prev => {
        const existingData = prev[layerId] || {} as EditorData<"map">;
        return {
          ...prev,
          [layerId]: {
            ...existingData,
            ...newData
          }
        };
      });
      // Increment cache version to trigger re-renders
      setCacheVersion(prev => prev + 1);
    },
    []
  );

  /**
   * Clears the global changes flag
   */
  const clearGlobalChangesFlag = useCallback(() => {
    setHasGlobalChanges(false);
  }, []);

  /**
   * Marks all layers as needing refresh due to global changes
   */
  const markAllLayersStale = useCallback(() => {
    setHasGlobalChanges(true);
  }, []);

  /**
   * Clears all cached data and resets state
   */
  const clearCache = useCallback(() => {
    setLayerDataCache({});
    setHasGlobalChanges(false);
  }, []);

  /**
   * Forces a complete reload of all data
   */
  const forceReload = useCallback(() => {
    setLayerDataCache({});
    setHasGlobalChanges(true);
  }, []);

  /**
   * Updates unused items in all cached layers
   */
  const updateUnusedItemsInCache = useCallback(async () => {
    if (!restAuth) return;
    
    const [unusedPanoLinks, unusedPoiLinks] = await Promise.all([
      mapPanoRepository.get_unused(restAuth),
      mapPoiRepository.get_unused(restAuth)
    ]);
    
    setLayerDataCache(prev => {
      const updated = { ...prev };
      
      Object.keys(updated).forEach(layerIdStr => {
        const layerId = parseInt(layerIdStr);
        updated[layerId] = {
          ...updated[layerId],
          unused_panorama_location: unusedPanoLinks,
          unused_poi_location: unusedPoiLinks,
        };
      });
      return updated;
    });
  }, [restAuth, mapPanoRepository, mapPoiRepository]);

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo(() => ({
    layerDataCache,
    hasGlobalChanges,
    getLayerData,
    updateLayerData,
    clearCache,
    forceReload,
    updateUnusedItemsInCache,
    markAllLayersStale,
    clearGlobalChangesFlag,
    cacheVersion,
  }), [
    layerDataCache, 
    hasGlobalChanges, 
    getLayerData, 
    updateLayerData, 
    clearCache, 
    forceReload, 
    updateUnusedItemsInCache, 
    markAllLayersStale, 
    clearGlobalChangesFlag,
    cacheVersion
  ]);

  return (
    <MapDataContext.Provider value={contextValue}>
      {children}
    </MapDataContext.Provider>
  );
};

/**
 * Hook to access map data context
 */
export const useMapData = () => {
  const context = useContext(MapDataContext);
  if (!context) {
    throw new Error('useMapData must be used within a MapDataProvider');
  }
  return context;
};